import type { ReactNode } from 'react';
import { cx } from '../../lib/cx';

/**
 * The same card shell `features/settings/fields.tsx` defines for the
 * workspace-admin Settings screen, redeclared locally rather than imported.
 * Settings is a different screen with a different owner this round, and its
 * `SectionCard` is not exported for reuse regardless — see that file's own
 * comment for why it took the same approach against `requests/RequestFields.tsx`
 * before it. Kept at the same visual weight (13px, `border-line`,
 * `rounded-xl`) so Account reads as the same product as Settings without the
 * two features reaching into each other's internals.
 */
export function SectionCard({
  title,
  description,
  children,
  actions,
  className,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('rounded-xl border border-line bg-base', className)}>
      <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h2 className="fs-13 font-semibold text-ink">{title}</h2>
          {description !== undefined && <p className="mt-0.5 fs-11.5 text-muted">{description}</p>}
        </div>
        {actions !== undefined && <div className="flex flex-none items-center gap-2">{actions}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
